import type React from 'react';
import { useMemo, useState } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid2 from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';

import Big from 'big.js';

import { invoicePaymentOption } from 'constants-ts/payments';
import { type PaymentTargetInfo } from '@a1/shared';

interface RefundDialogProps {
  open: boolean;
  onClose: () => void;
  onRefund: (invoiceId: number, externalRefNo: string, externalSource: string, refundAmount: string) => void;
  paymentOption: string;
  externalRefNo: string;
  externalSource: string;
  setExternalRefNo: React.Dispatch<React.SetStateAction<string>>;
  setExternalSource: React.Dispatch<React.SetStateAction<string>>;
  targetInfo: PaymentTargetInfo | null;
  customTitle?: string;
  loading?: boolean;
}

const RefundDialog: React.FC<RefundDialogProps> = ({
  open,
  onClose,
  onRefund,
  paymentOption,
  externalRefNo,
  externalSource,
  setExternalRefNo,
  setExternalSource,
  targetInfo,
  customTitle,
  loading
}: RefundDialogProps) => {
  const identifierToShow = targetInfo?.displayIdentifier || `ID: ${targetInfo?.id}`;
  const refundable = targetInfo?.refundable || 0;
  const defaultTitle = `Refund ${identifierToShow}`;
  const [refundAmount, setRefundAmount] = useState<string>('');

  const isAmountValid = useMemo(() => {
    if (!refundAmount) {
      return false;
    }
    const refundAmountBig = Big(refundAmount);
    return refundAmountBig.gt(0) && refundAmountBig.lte(refundable);
  }, [refundAmount, refundable]);

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h4">{customTitle || defaultTitle}</Typography>
      </DialogTitle>
      <DialogContent>
        {paymentOption === invoicePaymentOption.INVOICE.id && (
          <>
            <Typography variant="h5">External Refund Details</Typography>
            <Grid2 size={12} mt={2}>
              <TextField
                id="externalTransactionRef"
                type="text"
                label="External Transaction ID/Reference"
                fullWidth
                value={externalRefNo}
                onChange={(e) => {
                  setExternalRefNo(e.target.value);
                }}
              />
            </Grid2>
            <Grid2 size={12} mt={2}>
              <TextField
                id="externalSource"
                type="text"
                label="External Payment Method/Channel"
                fullWidth
                value={externalSource}
                onChange={(e) => {
                  setExternalSource(e.target.value);
                }}
              />
            </Grid2>
          </>
        )}
        <Grid2 size={12} mt={2}>
          <TextField
            id="refundAmount"
            type="number"
            label={`Refund Amount (${targetInfo?.currency ?? ''})`}
            fullWidth
            inputProps={{ min: 0, max: refundable.toString(), step: '0.01', inputMode: 'decimal' }}
            value={refundAmount}
            onChange={(e) => {
              const val = e.target.value;
              // Only allow numbers and up to 2 decimals
              if (/^\d*(\.\d{0,2})?$/.test(val)) {
                setRefundAmount(val);
              } else if (/^\d*\.\d+/.test(val)) {
                // If more than 2 decimals, trim
                const [whole, dec] = val.split('.');
                setRefundAmount(`${whole}.${dec.slice(0, 2)}`);
              }
            }}
            helperText={`Refundable: ${refundable.toFixed(2)} ${targetInfo?.currency ?? ''}`}
            error={!!refundAmount && !isAmountValid}
          />
        </Grid2>
        <Grid2 size={12} mt={2}>
          {paymentOption === invoicePaymentOption.INVOICE.id ? (
            <Alert severity="info">
              <b>Warning: This action is irreversible.</b> Please verify all details carefully and confirm the refund is justified before
              proceeding. Only complete this section for refund processed outside our system.
            </Alert>
          ) : (
            <Alert severity="info">
              <b>Warning: This action is irreversible.</b> This will issue a monetary refund to the customer. While we process it instantly,
              it can take awhile for the funds to reach their bank.
            </Alert>
          )}
        </Grid2>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            setExternalRefNo('');
            setExternalSource('');
            setRefundAmount('');
          }}
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          variant="contained"
          disabled={!isAmountValid}
          onClick={() => {
            if (!isAmountValid) {
              return;
            }
            onRefund(Number(targetInfo?.id), externalRefNo, externalSource, refundAmount);
            setRefundAmount('');
          }}
        >
          Refund
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundDialog;
